import * as React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';

export interface ConfirmOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: React.ReactNode;
  cancelText?: React.ReactNode;
  /** Red confirm button, for deleting, revoking, terminating. */
  destructive?: boolean;
  /**
   * Runs before the dialog closes; the confirm button spins while it is
   * pending and the dialog stays open if it throws, so the user can retry.
   */
  onConfirm?: () => Promise<unknown> | unknown;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

/**
 * Mounted once by `ThemeProvider`. Renders a single alert dialog driven by
 * `useConfirm()`, so a call site can write `if (await confirm({...}))` instead
 * of owning open state for every destructive action.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);
  const [busy, setBusy] = React.useState(false);

  const confirm = React.useCallback<ConfirmFn>(
    (options) => new Promise<boolean>((resolve) => setPending({ ...options, resolve })),
    [],
  );

  const close = (confirmed: boolean) => {
    pending?.resolve(confirmed);
    setPending(null);
    setBusy(false);
  };

  const onConfirm = async () => {
    if (!pending) return;
    if (!pending.onConfirm) {
      close(true);
      return;
    }
    setBusy(true);
    try {
      await pending.onConfirm();
      close(true);
    } catch {
      // The action reports its own error (a toast, usually); keep the dialog
      // open so the user can try again or back out.
      setBusy(false);
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !busy) close(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
            {pending?.description ? (
              <AlertDialogDescription>{pending.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{pending?.cancelText ?? 'Cancel'}</AlertDialogCancel>
            <Button
              variant={pending?.destructive ? 'destructive' : 'default'}
              loading={busy}
              onClick={() => void onConfirm()}
            >
              {pending?.confirmText ?? 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const confirm = React.useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside ThemeProvider');
  return confirm;
}
